import React, { useState, useEffect, useCallback, useRef } from 'react'

import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { Card, Table, Button, Modal, Form, Input, InputNumber, Select, Space, message, Popconfirm, Divider, Typography } from 'antd'
import debounce from 'lodash/debounce'
import { useTranslation } from 'react-i18next'

/**
 * Generic Value Added Services Card Component
 * Used for both Orders and Inbounds
 *
 * @param {Object} props
 * @param {Object} props.record - The parent record (order or inbound)
 * @param {Object} props.vasService - Service object with { add, update, delete } methods
 * @param {Object} props.valueAddedServiceService - Service for searching VAS templates (with fetchList method)
 * @param {Function} props.onUpdate - Callback when data is updated
 * @param {boolean} props.editable - Whether editing is allowed
 */
export default function ValueAddedServicesCard({
  record,
  vasService,
  valueAddedServiceService,
  onUpdate,
  editable = true,
  showCard = true,
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const priceValue = Form.useWatch('price', form)
  const quantityValue = Form.useWatch('quantity', form)
  const previewSubtotal = (priceValue || 0) * (quantityValue || 0)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [loading, setLoading] = useState(false)
  const [services, setServices] = useState(record?.value_added_services || [])

  // 範本搜尋相關狀態
  const [templateOptions, setTemplateOptions] = useState([])
  const [templateLoading, setTemplateLoading] = useState(false)
  const templateCacheRef = useRef({})

  // 兼容頂層 merchant_id（入庫 payload）與巢狀 merchant.id（訂單 payload）
  const merchantId = record?.merchant_id ?? record?.merchant?.id

  useEffect(() => {
    setServices(record?.value_added_services || [])
  }, [record?.value_added_services])

  // 載入範本列表
  const fetchTemplates = useCallback(async (keyword = '') => {
    if (!valueAddedServiceService) return

    setTemplateLoading(true)
    try {
      const result = await valueAddedServiceService.fetchList({
        search: keyword,
        merchant_id: merchantId,
        page_size: 20
      })
      if (result.success && result.data) {
        const options = result.data.map(item => ({
          value: item.id,
          label: `${item.title} ($${parseFloat(item.default_price).toFixed(2)})`,
          title: item.title,
          default_price: item.default_price
        }))
        setTemplateOptions(options)
        // 緩存範本資料
        result.data.forEach(item => {
          templateCacheRef.current[item.id] = item
        })
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err)
    } finally {
      setTemplateLoading(false)
    }
  }, [valueAddedServiceService, merchantId])

  // 搜尋範本 (debounced)
  const searchTemplates = useCallback(
    debounce((keyword) => fetchTemplates(keyword), 300),
    [fetchTemplates]
  )

  // 下拉選單開啟時預載
  const handleDropdownVisibleChange = useCallback((open) => {
    if (open && templateOptions.length === 0) {
      fetchTemplates()
    }
  }, [fetchTemplates, templateOptions.length])

  const handleAdd = useCallback(() => {
    setEditingRecord(null)
    form.resetFields()
    form.setFieldsValue({ quantity: 1 })
    setTemplateOptions([])
    setModalVisible(true)
  }, [form])

  const handleEdit = useCallback((item) => {
    setEditingRecord(item)
    form.setFieldsValue({
      merchant_value_added_service_id: item.merchant_value_added_service_id,
      title: item.title,
      price: item.price,
      quantity: item.quantity,
    })
    // 如果有關聯的範本，預先設置選項
    if (item.merchant_value_added_service_id) {
      setTemplateOptions([{
        value: item.merchant_value_added_service_id,
        label: item.title,
        title: item.title,
        default_price: item.price
      }])
    } else {
      setTemplateOptions([])
    }
    setModalVisible(true)
  }, [form])

  const handleDelete = useCallback(async (item) => {
    try {
      setLoading(true)
      const result = await vasService.delete(record.id, item.id)
      if (result.success) {
        message.success(t('common.message.delete_success'))
        setServices(prev => prev.filter(s => s.id !== item.id))
        onUpdate?.()
      } else {
        const errorKey = `common.error.${result.message}`
        const translated = t(errorKey)
        message.error(translated !== errorKey ? translated : t('common.error.operation_failed'))
      }
    } catch (err) {
      console.error('Failed to delete value added service:', err)
      message.error(t('common.error.operation_failed'))
    } finally {
      setLoading(false)
    }
  }, [record?.id, vasService, onUpdate, t])

  const handleModalOk = useCallback(async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      let result
      if (editingRecord) {
        result = await vasService.update(record.id, editingRecord.id, values)
      } else {
        result = await vasService.add(record.id, values)
      }

      if (result.success) {
        message.success(editingRecord
          ? t('common.message.update_success')
          : t('common.message.create_success')
        )
        setModalVisible(false)
        onUpdate?.()
        // Update local state
        if (result.data) {
          if (editingRecord) {
            setServices(prev => prev.map(s => s.id === editingRecord.id ? result.data : s))
          } else {
            setServices(prev => [...prev, result.data])
          }
        }
      } else {
        const errorKey = `common.error.${result.message}`
        const translated = t(errorKey)
        message.error(translated !== errorKey ? translated : t('common.error.operation_failed'))
      }
    } catch (err) {
      if (err.errorFields) {
        return // form validation error
      }
      console.error('Failed to save value added service:', err)
      message.error(t('common.error.operation_failed'))
    } finally {
      setLoading(false)
    }
  }, [form, editingRecord, record?.id, vasService, onUpdate, t])

  const handleTemplateChange = useCallback((serviceId, option) => {
    if (!serviceId) {
      form.setFieldsValue({ title: '', price: undefined })
      return
    }
    // 優先使用 option 中的資料，或從緩存中取得
    const template = option || templateCacheRef.current[serviceId]
    if (template) {
      form.setFieldsValue({
        title: template.title,
        price: template.default_price,
      })
    }
  }, [form])

  const columns = [
    {
      title: t('models.merchant_value_added_service.title'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('models.merchant_value_added_service.price'),
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-',
    },
    {
      title: t('models.merchant_value_added_service.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
    },
    {
      title: t('models.merchant_value_added_service.subtotal'),
      key: 'subtotal',
      align: 'right',
      render: (_, item) => {
        const subtotal = (item.price || 0) * (item.quantity || 0)
        return `$${subtotal.toFixed(2)}`
      },
    },
  ]

  if (editable) {
    columns.push({
      title: t('common.table.actions'),
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, item) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(item)}
          />
          <Popconfirm
            title={t('common.confirm.delete')}
            onConfirm={() => handleDelete(item)}
            okText={t('common.button.confirm')}
            cancelText={t('common.button.cancel')}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    })
  }

  const total = services.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0)

  const tableContent = (
    <>
      {!showCard && editable && (
        <div style={{ marginBottom: 12, textAlign: 'right' }}>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            {t('common.button.add')}
          </Button>
        </div>
      )}
      <Table
        dataSource={services}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        loading={loading}
        locale={{ emptyText: t('common.table.no_data') }}
        summary={() => services.length > 0 && (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <strong>{t('models.merchant_value_added_service.total')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} />
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3} align="right">
                <strong>${total.toFixed(2)}</strong>
              </Table.Summary.Cell>
              {editable && <Table.Summary.Cell index={4} />}
            </Table.Summary.Row>
            </Table.Summary>
          )}
        />
    </>
  )

  return (
    <>
      {showCard ? (
        <Card
          title={t('models.merchant_value_added_service._plural')}
          style={{ marginBottom: '16px' }}
          size="small"
          extra={editable && (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('common.button.add')}
            </Button>
          )}
        >
          {tableContent}
        </Card>
      ) : (
        tableContent
      )}

      <Modal
        title={editingRecord
          ? t('models.merchant_value_added_service.modal.edit_title')
          : t('models.merchant_value_added_service.modal.add_title')
        }
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
        >
          {valueAddedServiceService && (
            <Form.Item
              name="merchant_value_added_service_id"
              label={t('models.merchant_value_added_service.select_template')}
            >
              <Select
                showSearch
                allowClear
                placeholder={t('models.merchant_value_added_service.select_template_placeholder')}
                filterOption={false}
                onDropdownVisibleChange={handleDropdownVisibleChange}
                onSearch={searchTemplates}
                onChange={handleTemplateChange}
                loading={templateLoading}
                options={templateOptions}
                notFoundContent={templateLoading ? t('common.loading') : t('common.table.no_data')}
              />
            </Form.Item>
          )}

          <Form.Item
            name="title"
            label={t('models.merchant_value_added_service.title')}
            rules={[{ required: true, message: t('models.merchant_value_added_service.validation.title_required') }]}
          >
            <Input placeholder={t('models.merchant_value_added_service.placeholder.title')} />
          </Form.Item>

          <Form.Item
            name="price"
            label={t('models.merchant_value_added_service.price')}
            rules={[{ required: true, message: t('models.merchant_value_added_service.validation.price_required') }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              prefix="$"
              placeholder={t('models.merchant_value_added_service.placeholder.price')}
            />
          </Form.Item>

          <Form.Item
            name="quantity"
            label={t('models.merchant_value_added_service.quantity')}
            rules={[{ required: true, message: t('models.merchant_value_added_service.validation.quantity_required') }]}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder={t('models.merchant_value_added_service.placeholder.quantity')}
            />
          </Form.Item>

          {(priceValue > 0 || quantityValue > 0) && (
            <>
              <Divider style={{ margin: '16px 0 12px' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text type="secondary">
                  {t('models.merchant_value_added_service.subtotal')}
                </Typography.Text>
                <Typography.Text strong style={{ fontSize: '16px' }}>
                  ${previewSubtotal.toFixed(2)}
                </Typography.Text>
              </div>
            </>
          )}
        </Form>
      </Modal>
    </>
  )
}
